import { otNavigation } from '../../../otlWebEvents';
import { trace } from '@opentelemetry/api';
import { context as otContext } from '@opentelemetry/api';
import { createAppRootSpan } from '../../../utils';

/**
 * A service for managing root and navigation OpenTelemetry spans and contexts in the application.
 *
 * Handles creation, activation, and cleanup of application-wide root and navigation traces,
 * and ensures proper context propagation for child spans.
 */
export class TraceContextService {
    /**
     * Internal storage for the root trace span and its context.
     * @type {{ _activeSpan: import('@opentelemetry/api').Span|null, _parentContext: import('@opentelemetry/api').Context|null }}
     */
    _rootTrace = {
        _activeSpan: null,
        _parentContext: null,
    };
    /**
     * Internal storage for the navigation trace span and its context.
     * @type {{ _activeSpan: import('@opentelemetry/api').Span|null, _parentContext: import('@opentelemetry/api').Context|null }}
     */
    _navigationTrace = {
        _activeSpan: null,
        _parentContext: null,
    };

    /**
     * Initializes the root trace span, sets up the navigation trace for the current page,
     * and attaches a listener for page unload to ensure spans are ended.
     */
    init() {
        const span = createAppRootSpan();
        this._rootTrace._activeSpan = span;
        this._rootTrace._parentContext = trace.setSpan(otContext.active(), span);
        this.startNavigationTrace(window.location.pathname);
        window.addEventListener('beforeunload', () => {
            this.clear('_navigationTrace');
            this.clear('_rootTrace');
        });
    }

    /**
     * Starts a new navigation trace span for the given path, ending any previous navigation span.
     * @param {string} path - The pathname for the navigation span.
     */
    startNavigationTrace(path) {
        try {
            if (!path) {
                return;
            }

            if (this._navigationTrace._activeSpan) {
                this.clear('_navigationTrace');
            }

            const span = otNavigation(path, this._rootTrace._parentContext);
            this._navigationTrace._activeSpan = span;
            this._navigationTrace._parentContext = trace.setSpan(otContext.active(), span);
            // eslint-disable-next-line no-empty
        } catch (e) {}
    }

    /**
     * Returns the currently active parent context (navigation > root > global context).
     * @returns {import('@opentelemetry/api').Context}
     */
    getActiveParentContext() {
        return this._navigationTrace._parentContext || this._rootTrace._parentContext || otContext.active();
    }

    /**
     * Ends and clears the specified span and context (either '_rootTrace' or '_navigationTrace').
     * @param {'_rootTrace'|'_navigationTrace'} path - The key of the trace to clear.
     */
    clear(path) {
        if (this[path]._activeSpan) {
            this[path]._activeSpan.end();
            this[path]._activeSpan = null;
        }
        this[path]._parentContext = null;
    }
}
